// Het vlak Kwaliteit (W6 ronde 5, besluit V1; research §3.6).
//
// Sinds D1 (01-09) publiceert het systeem trends zonder dat een mens ze eerst
// ziet. Dit vlak meet achteraf of de machine nog goed werkt, in twee tabbladen:
//  · De meters — de steekproef (met de beoordeellijst), de chat-benchmark, wat
//    nog niet gemeten wordt, de funding-extractie en de telling van de
//    chatlogs (GET /api/beheer/kwaliteit);
//  · Chatlogs  — de bewaarde gesprekken zelf (GET /api/beheer/gesprekken).
//
// De regel die hier het zwaarst weegt: geen verzonnen drempel. Voor de
// steekproef en de benchmark heeft de owner (nog) geen grens vastgesteld; de
// server zet dan de stand "leeg" mét die reden, en dit vlak toont precies dat.
// Het rekent geen aandeel, geen verschil en geen stoplicht uit.
//
// V2 (bewaartermijn): de inhoud van gesprekken staat alleen onder Chatlogs, en
// dat tabblad haalt pas op als het open staat — wie de meters komt bekijken,
// krijgt geen gesprekken mee.

import { useEffect } from "react";

import { useAdresParam } from "../../lib/adres";
import { api } from "../../lib/api";
import { useResource } from "../../lib/useResource";
import type { KwaliteitResponse } from "../../contracts";
import { color, radius } from "../../tokens";
import { Chatlogs } from "./KwaliteitChatlogs";
import { Meters } from "./KwaliteitMeters";

/** De ascii-sleutel reist mee naar het gebruikslog ("Beheer/Kwaliteit/Chatlogs");
 *  het label is wat de beheerder leest. */
type TabKey = "Meters" | "Chatlogs";

const TABS: readonly { key: TabKey; label: string }[] = [
  { key: "Meters", label: "De meters" },
  { key: "Chatlogs", label: "Chatlogs" },
];

export function Kwaliteit() {
  // Het tabblad staat in het adres (?kwaliteit=Chatlogs); zonder of bij onzin: de meters.
  const [tabParam, zetTabParam] = useAdresParam("kwaliteit");
  const tab: TabKey = TABS.find((t) => t.key === tabParam)?.key ?? "Meters";
  const setTab = (t: TabKey) => zetTabParam(t === "Meters" ? null : t, "push");
  // De meters halen op zodra hun tabblad open staat. Terug van de chatlogs
  // vragen ze opnieuw: een oordeel of een nieuw gesprek kan de telling hebben
  // veranderd, en een oud getal naast een nieuw gesprek leest als een fout.
  const kwaliteit = useResource<KwaliteitResponse>(() => api.kwaliteit(), [], tab === "Meters");

  // Welk tabblad, zoals de schil het vlak meldt (W6, research §4): alleen WAAR
  // iemand kijkt, nooit wat erin staat. Ook het eerste tabblad telt.
  // Vuur-en-vergeet: een telling die faalt mag het scherm nooit raken.
  useEffect(() => {
    void api.gebruik({ scherm: `Beheer/Kwaliteit/${tab}` }).catch(() => {});
  }, [tab]);

  return (
    <div>
      {/* Hetzelfde gesegmenteerde patroon als de subnavigatie van Beheer. */}
      <nav
        aria-label="Onderdelen van Kwaliteit"
        style={{
          display: "inline-flex",
          flexWrap: "wrap",
          gap: 4,
          background: color.lineFaint,
          borderRadius: radius.input,
          padding: 3,
          marginBottom: 18,
        }}
      >
        {TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            className={"view-btn" + (tab === t.key ? " active" : "")}
            aria-pressed={tab === t.key}
            onClick={() => setTab(t.key)}
          >
            {t.label}
          </button>
        ))}
      </nav>

      {tab === "Meters" ? <Meters bron={kwaliteit} /> : <Chatlogs />}
    </div>
  );
}
